import React, { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { MfaStatusType } from '@upstart/harmony/lib/legos/constants/user';
import logo from '../../assets/upstart-white.png';
import { mfaLogin } from '../../redux/auth/thunks';
import { useAppDispatch, useTypedSelector } from '../../redux/store';
import { Button } from '../Input/Button/Button';
import { TextField } from '../Input/TextField/TextField';
import { LoginError } from './LoginError';
import styles from './styles.module.scss';
import { LockedOutModal } from './LockedOutModal';
import useOktaLogin from './useOktaLogin';

interface FormData {
  email: string;
  password: string;
}

export const Login: FunctionComponent = () => {
  const form = useForm<FormData>({ mode: 'onTouched' });
  const dispatch = useAppDispatch();
  const authStore = useTypedSelector((state) => state.auth);
  const okta = useOktaLogin({ email: form.watch('email', '').trim() });

  const errorMsg = authStore.error.message ? authStore.error.message : '';
  const useOkta =
    okta.required || authStore.error.status === MfaStatusType.OIDC_REQUIRED;

  const onSubmit = async (data: FormData) => {
    if (useOkta) {
      okta.redirect();
    } else {
      await dispatch(mfaLogin(data));
    }
  };

  const showLockedOutModal =
    authStore.error.status === MfaStatusType.ACCOUNT_LOCKED;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <img src={logo} alt="Logo" />
        Launch Pad
      </div>
      {!showLockedOutModal && (
        <form
          className={styles.loginContainer}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className={styles.loginTitle}>Sign In To Your Account</div>
          <div className={styles.loginSubtitle}>
            This account will help us keep track of your progress so you can
            move forward at your own pace.
          </div>
          <LoginError error={errorMsg} />
          <TextField
            className={styles.emailInput}
            label="Email"
            name="email"
            form={form}
            validation={{ required: true }}
          />
          {!useOkta && (
            <TextField
              label="Password"
              name="password"
              type="password"
              allowAutofill
              form={form}
              validation={{ required: true }}
            />
          )}
          <Button
            className={styles.loginButton}
            label="Sign In"
            type="submit"
            variant="primary"
            loading={form.formState.isSubmitting || authStore.fetching}
          />
        </form>
      )}
      {showLockedOutModal && <LockedOutModal lockedOutMsg={errorMsg} />}
      <div className="spacer" />
    </div>
  );
};
